import { useEffect, useMemo, useState } from "react";
import { Select, Tooltip } from "antd";
import toast from "react-hot-toast";

import Table from "../../components/Tables/Table";
import TableHeader from "../../components/Tables/TableHeader";

import { getStudentStatistics } from "../../services/studentStatisticsService";
import { getSessions } from "../../services/sessionService";

const { Option } = Select;

const StudentStatsTab = ({ institution }) => {
  const [stats, setStats] = useState([]);
  const [filteredStats, setFilteredStats] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState("Not Selected");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [expandedRowKey, setExpandedRowKey] = useState(null);

  useEffect(() => {
    if (!institution?.id) return;
    loadAll(institution.id);
  }, [institution?.id]);

  const loadAll = async (institutionId) => {
    setLoading(true);
    try {
      const [sessRes, statRes] = await Promise.all([
        getSessions(),
        getStudentStatistics(),
      ]);
      const allSessions = sessRes?.data?.data || [];
      const allStats = statRes?.data?.data || [];

      // Keep only rows for this institution
      const scoped = allStats.filter(
        (s) => s.institution?.id === institutionId
      );

      setSessions(allSessions);
      setStats(scoped);
      setFilteredStats(scoped);
    } catch {
      toast.error("Failed to load student statistics");
    } finally {
      setLoading(false);
    }
  };

  // Filters (search on session name / category name)
  useEffect(() => {
    let filtered = stats;

    if (searchTerm.trim() !== "") {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((row) => {
        const sessionName = (row.session?.name || "").toLowerCase();
        const catName = (row.student_category?.name || "").toLowerCase();
        return sessionName.includes(term) || catName.includes(term);
      });
    }

    if (selectedSession !== "Not Selected") {
      filtered = filtered.filter(
        (item) => item.session?.id?.toString() === selectedSession?.toString()
      );
    }

    setFilteredStats(filtered);
  }, [searchTerm, selectedSession, stats]);

  // Group by Session (like your Students page)
  const groupedStats = useMemo(() => {
    const grouped = new Map();
    for (const item of filteredStats) {
      const sess = item.session;
      if (!sess) continue;

      const key = `${institution.id}__${sess.id}`;
      if (!grouped.has(key)) {
        grouped.set(key, {
          key,
          institution,
          session: sess,
          data: [],
          totals: {
            humanities_male_count: 0, // number_of_males
            humanities_female_count: 0, // number_of_females
            sciences_male_count: 0,
            sciences_female_count: 0,
            special_needs: 0,
            total: 0,
          },
        });
      }
      const grp = grouped.get(key);
      grp.data.push(item);

      const humM = item.number_of_males || 0;
      const humF = item.number_of_females || 0;
      const sciM = item.sciences_number_of_males || 0;
      const sciF = item.sciences_number_of_females || 0;
      const spN = item.number_of_students_with_special_needs || 0;

      grp.totals.humanities_male_count += humM;
      grp.totals.humanities_female_count += humF;
      grp.totals.sciences_male_count += sciM;
      grp.totals.sciences_female_count += sciF;
      grp.totals.special_needs += spN;
      grp.totals.total += humM + humF + sciM + sciF;
    }
    return Array.from(grouped.values());
  }, [filteredStats, institution]);

  const columns = [
    {
      title: "Year",
      dataIndex: ["session", "name"],
      width: 160,
    },
    {
      title: "Male (Humanities)",
      dataIndex: ["totals", "humanities_male_count"],
      width: 160,
    },
    {
      title: "Female (Humanities)",
      dataIndex: ["totals", "humanities_female_count"],
      width: 160,
    },
    {
      title: "Male (Sciences)",
      dataIndex: ["totals", "sciences_male_count"],
      width: 160,
    },
    {
      title: "Female (Sciences)",
      dataIndex: ["totals", "sciences_female_count"],
      width: 160,
    },
    {
      title: "Special Needs",
      dataIndex: ["totals", "special_needs"],
      width: 160,
    },
    {
      title: "Total",
      dataIndex: ["totals", "total"],
      width: 160,
    },
  ];

  return (
    <div className="pt-2">
      <Table
        isPaginable
        pageSize={10}
        isSearchable={false}
        loading={loading}
        columns={columns}
        rows={groupedStats}
        expandable={{
          expandedRowKeys: expandedRowKey ? [expandedRowKey] : [],
          onExpand: (expanded, record) => {
            setExpandedRowKey(expanded ? record.key : null);
          },
          expandedRowRender: (record) => (
            <table className="w-full">
              <thead className="bg-gray-100">
                <tr>
                  <th className="text-left p-2">Student Category</th>
                  <th className="text-left p-2">Male (Humanities)</th>
                  <th className="text-left p-2">Female (Humanities)</th>
                  <th className="text-left p-2">Male (Sciences)</th>
                  <th className="text-left p-2">Female (Sciences)</th>
                  <th className="text-left p-2">Special Needs</th>
                  <th className="text-left p-2">Total</th>
                </tr>
              </thead>
              <tbody className="bg-amber-50">
                {record.data.map((item) => (
                  <tr key={item.id}>
                    <td className="p-2 text-sm">
                      {item.student_category?.name || "—"}
                    </td>
                    <td className="p-2 text-sm">{item.number_of_males}</td>
                    <td className="p-2 text-sm">{item.number_of_females}</td>
                    <td className="p-2 text-sm">
                      {item.sciences_number_of_males}
                    </td>
                    <td className="p-2 text-sm">
                      {item.sciences_number_of_females}
                    </td>
                    <td className="p-2 text-sm">
                      {item.number_of_students_with_special_needs || 0}
                    </td>
                    <td className="p-2 text-sm">
                      {(item.number_of_males || 0) +
                        (item.number_of_females || 0) +
                        (item.sciences_number_of_males || 0) +
                        (item.sciences_number_of_females || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ),
        }}
        extra={
          <div className="mb-2 py-2 flex gap-2">
            <Select
              multiple={false}
              id="sessions"
              defaultValue={selectedSession}
              className="w-full h-[36px] min-w-[250px]"
              onChange={(value) => setSelectedSession(value)}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase().includes(input.toLowerCase())
              }
            >
              <Option value={"Not Selected"}>Filter by Year</Option>
              {sessions.map((s) => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </div>
        }
      />
    </div>
  );
};

export default StudentStatsTab;
